import Colors from '@constants/Colors';
import { EventAgendaSection } from '@organisms/EventAgendaSection';
import { EventLandingHeader } from '@organisms/EventLandingHeader';
import { EventLocationSection } from '@organisms/EventLocationSection';
import { EventSidebar } from '@organisms/EventSidebar';
import {
  useGetCommunityBySlug,
  useGetEventByCommunityId,
  useGetEventLocation,
  useGetSessionsByEventId,
} from '@sunrise-ui/api-client';
import { SessionDetailsModal } from '@sunrise-ui/composites';
import {
  Button,
  ExpandableText,
  Loader,
  TextBold,
  TextReg,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { router } from 'expo-router';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

type Props = {
  slug: string;
};

/**
 * EventLandingView - Main landing page template for events
 *
 * Composes:
 * - EventLandingHeader (hero with image, title, date, location, CTA)
 * - About section (event description)
 * - EventAgendaSection (session cards, max 3 + "See All")
 * - EventLocationSection (venue details + maps link)
 */
export function EventLandingView({ slug }: Props) {
  const styles = useDynamicStyles(createStyles);
  const { isMobile } = useMediaQueries();

  // Fetch community by slug to get community ID
  const { data: community, isLoading: isLoadingCommunity } =
    useGetCommunityBySlug(slug);

  // Fetch event details using community ID
  const { data: event, isLoading: isLoadingEventData } =
    useGetEventByCommunityId(community?.id);

  const isLoadingEvent = isLoadingCommunity || isLoadingEventData;

  // Fetch location details
  const { data: location, isLoading: isLoadingLocation } = useGetEventLocation(
    event?.event_location_id,
  );

  // Fetch sessions for agenda section
  const { data: sessions, isLoading: isLoadingSessions } =
    useGetSessionsByEventId(event?.id);

  // Loading state
  if (isLoadingEvent) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <Loader />
        </View>
      </View>
    );
  }

  // Error state
  if (!event) {
    return (
      <View style={styles.container}>
        <View style={styles.errorContainer}>
          <TextReg style={styles.errorText}>Event not found</TextReg>
          <Button onPress={() => router.back()}>Back to Events</Button>
        </View>
      </View>
    );
  }

  // Shared content sections used in both mobile and desktop layouts
  const AboutSection = event.description ? (
    <View style={styles.aboutSection}>
      <TextBold style={styles.sectionTitle}>About Event</TextBold>
      <View style={styles.aboutTextContainer}>
        <ExpandableText text={event.description} charLimit={800} />
      </View>
    </View>
  ) : null;

  const AgendaSection =
    sessions && sessions.length > 0 && event.id ? (
      <EventAgendaSection
        eventId={event.id}
        sessions={sessions}
        isLoading={isLoadingSessions}
        communityId={event.community_id}
      />
    ) : null;

  const LocationSection = (
    <EventLocationSection location={location} isLoading={isLoadingLocation} />
  );

  return (
    <>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {isMobile ? (
          // Mobile: single column stacked layout
          <View style={styles.mobileContainer}>
            <EventLandingHeader
              event={event}
              isLoading={isLoadingEvent}
              showPresentedBy={true}
            />
            {AboutSection}
            {AgendaSection}
            {LocationSection}
          </View>
        ) : (
          // Desktop: two-column layout
          <View style={styles.twoColumnContainer}>
            {/* Left column: Image + Sidebar */}
            <View style={styles.leftColumn}>
              <EventLandingHeader
                event={event}
                isLoading={isLoadingEvent}
                layout="image"
              />
              <EventSidebar event={event} />
            </View>

            {/* Right column: Info + About + Agenda + Location */}
            <View style={styles.rightColumn}>
              <EventLandingHeader
                event={event}
                isLoading={isLoadingEvent}
                layout="info"
              />
              {AboutSection}
              {AgendaSection}
              {LocationSection}
            </View>
          </View>
        )}
      </ScrollView>

      {/* Session Details Modal */}
      <SessionDetailsModal communityId={event.community_id} />
    </>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: Colors.dark.purple0,
    },
    scrollView: {
      flex: 1,
      backgroundColor: Colors.dark.purple0,
    },
    scrollContent: {
      paddingBottom: isMobile ? 40 : 60,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      gap: 16,
    },
    errorText: {
      color: Colors.dark.text,
      fontSize: 16,
    },
    // Two-column layout for desktop
    twoColumnContainer: {
      flexDirection: 'row',
      width: '100%',
      maxWidth: 1200,
      alignSelf: 'center',
      paddingHorizontal: 40,
      paddingTop: 32,
      gap: 32,
    },
    leftColumn: {
      width: 320,
      flexShrink: 0,
      gap: 32,
    },
    rightColumn: {
      flex: 1,
      minWidth: 0,
      gap: 32,
    },
    mobileContainer: {
      gap: 24,
    },
    aboutSection: {
      width: '100%',
      paddingHorizontal: isMobile ? 16 : 0,
      gap: isMobile ? 12 : 16,
      alignItems: 'flex-start',
    },
    sectionTitle: {
      fontSize: isMobile ? 20 : 24,
      color: Colors.dark.text,
      textAlign: 'left',
    },
    aboutTextContainer: {
      maxWidth: isMobile ? 600 : undefined,
      width: '100%',
      alignItems: 'flex-start',
    },
  });
